import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { usePost } from "../../API";
import { ChatSessionContext } from "../ChatSessionContext";
import { captureException } from "@sentry/react";
import { ApplicationStateContext } from "../ApplicationStateContext";
import { ChatMessageItem } from "@aidkitorg/types/lib/convoai";
import { ChatContextType } from "./ChatContributor";
import { ChatConversationContext, ChatPhase } from "./ConversationContext";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";

export function useOpenAITextChat(): ChatContextType {

  const { dispatch, latestMessage } = useContext(ChatConversationContext);
  const { state: appState } = useContext(ApplicationStateContext);
  const { conversationId, loaded } = useContext(ChatSessionContext);
  const chat = usePost('/ai/chat/message');
  const [lastQueryAnswered, setLastQueryAnswered] = useState<number>(latestMessage?.index || -1 /* ensures we reply to 0th index user messages */);

  useEffect(() => {

    let phase: ChatPhase = 'unknown';
    if (loaded) {
      if (conversationId) {
        phase = 'listening';
      }
    } else {
      phase = 'initializing';
    }

    dispatch({ type: 'update_phase', phase });
  }, [loaded, conversationId]);

  const askAssistant = useCallback(async (update: ChatMessageItem) => {
    try {
      dispatch({ type: 'update_phase', phase: 'thinking' });
      const response = await chat({
        conversationId: conversationId!,
        appState,
        newMessages: [update]
      });

      for (const part of response) {
        dispatch(part);
      }
    } catch (e: any) {
      dispatch({
        type: 'error',
        error: {
          type: 'request_error',
          message: e.message
        }
      });
      captureException(e, {
        ...e,
        conversationId
      });
    } finally {
      dispatch({ type: 'update_phase', phase: 'listening' });
    }
  }, [conversationId, appState, chat]);

  useAsyncEffect(async () => {

    // indicates that the conversation has reset in some way.
    // resetting lastQueryAnswered so that we can take action on the new messages
    if (!latestMessage) {
      setLastQueryAnswered(-1);
      return;
    }

    const { message, index } = latestMessage;

    if (message.type === 'message' && message.role === 'user') {
      if (index > lastQueryAnswered) {
        setLastQueryAnswered(index);
        await askAssistant(message);
      }
    }

  }, [latestMessage, askAssistant]);

  return {
    active: true,
    ready: true,
    enabled: true,
    setEnabled() {},
    sendMessage: dispatch,
  }
}
